import React, { useState } from 'react';
import { useLanguage } from '@/components/ui/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  HelpCircle, 
  MessageCircle, 
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Mail,
  Video,
  CreditCard,
  Subtitles,
  Wrench
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

export default function Help() {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('faq');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [contactForm, setContactForm] = useState({
    email: '',
    subject: '',
    message: ''
  });

  const faqs = [
    {
      category: 'video',
      icon: Video,
      question: language === 'ja' ? '動画が再生できません' : 'Video won\'t play',
      answer: language === 'ja' 
        ? 'まず、インターネット接続を確認してください。問題が解決しない場合は、ブラウザのキャッシュをクリアするか、別のブラウザでお試しください。それでも解決しない場合はお問い合わせください。'
        : 'First, check your internet connection. If the problem persists, try clearing your browser cache or using a different browser. If it still doesn\'t work, please contact us.'
    },
    {
      category: 'subtitles',
      icon: Subtitles,
      question: language === 'ja' ? '字幕が表示されません' : 'Subtitles not showing',
      answer: language === 'ja'
        ? '動画プレーヤーの字幕ボタンをクリックして、希望の言語を選択してください。一部の動画では字幕が利用できない場合があります。'
        : 'Click the subtitle button in the video player and select your preferred language. Some videos may not have subtitles available.'
    },
    {
      category: 'payment',
      icon: CreditCard,
      question: language === 'ja' ? '支払いに失敗しました' : 'Payment failed',
      answer: language === 'ja'
        ? 'カード情報が正しいことを確認してください。問題が続く場合は、別の支払い方法をお試しいただくか、カード会社にお問い合わせください。マイページからStripeポータルにアクセスして支払い情報を更新することもできます。'
        : 'Please verify your card information is correct. If the problem continues, try a different payment method or contact your card provider. You can also update your payment information through the Stripe portal from your account page.'
    },
    {
      category: 'payment',
      icon: CreditCard,
      question: language === 'ja' ? 'サブスクリプションをキャンセルしたい' : 'How to cancel subscription',
      answer: language === 'ja'
        ? 'マイページの「課金/契約」タブから「サブスク管理」ボタンをクリックしてください。Stripeポータルでサブスクリプションをキャンセルできます。キャンセル後も、現在の請求期間の終了まで引き続きサービスをご利用いただけます。'
        : 'Go to the Subscription tab on your account page and click "Manage Subscription". You can cancel your subscription through the Stripe portal. After cancellation, you can continue using the service until the end of your current billing period.'
    },
    {
      category: 'video',
      icon: Video,
      question: language === 'ja' ? '動画の画質を変更するには' : 'How to change video quality',
      answer: language === 'ja'
        ? '動画プレーヤーの設定アイコン（歯車マーク）をクリックして、希望の画質を選択してください。「自動」を選択すると、お使いのインターネット速度に応じて最適な画質が自動的に選択されます。'
        : 'Click the settings icon (gear) in the video player and select your preferred quality. Selecting "Auto" will automatically choose the best quality based on your internet speed.'
    },
    {
      category: 'general',
      icon: HelpCircle,
      question: language === 'ja' ? 'Discord連携について' : 'About Discord integration',
      answer: language === 'ja'
        ? 'Discordでログインすると、学習仲間とのコミュニティに参加でき、学習時間の記録やバッジの獲得が可能になります。Discordサーバーへの参加は任意ですが、他の学習者との交流や質問ができるのでおすすめです。'
        : 'By logging in with Discord, you can join our community of learners, track your study time, and earn badges. Joining the Discord server is optional, but recommended for interacting with other learners and asking questions.'
    }
  ];

  const incidents = [
    {
      date: '2024-01-15',
      status: 'resolved',
      title: language === 'ja' ? 'サーバーメンテナンス完了' : 'Server maintenance completed',
      description: language === 'ja' 
        ? '予定されていたメンテナンスが完了しました。すべてのサービスが正常に稼働しています。'
        : 'Scheduled maintenance has been completed. All services are operating normally.'
    }
  ];

  const handleSubmitContact = (e) => {
    e.preventDefault();
    // In a real app, this would send the form data
    alert(language === 'ja' ? 'お問い合わせを送信しました' : 'Your message has been sent');
    setContactForm({ email: '', subject: '', message: '' });
  };

  const getCategoryLabel = (category) => {
    const labels = {
      video: language === 'ja' ? '動画再生' : 'Video',
      subtitles: language === 'ja' ? '字幕' : 'Subtitles',
      payment: language === 'ja' ? '支払い' : 'Payment',
      general: language === 'ja' ? '一般' : 'General'
    };
    return labels[category] || category;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810] pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#8b6f47]/20 flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-[#8b6f47]" />
          </div>
          <h1 className="text-3xl font-bold mb-2 text-[#2d1810]">{t('help')}</h1>
          <p className="text-[#5d4529]">
            {language === 'ja' ? 'お困りの際はこちらをご確認ください' : 'Check here if you need help'}
          </p>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="w-full bg-white p-1">
            <TabsTrigger value="faq" className="flex-1 data-[state=active]:bg-[#8b6f47] data-[state=active]:text-[#f5ede3] text-[#5d4529]">
              <HelpCircle className="w-4 h-4 mr-2" />
              {t('faq')}
            </TabsTrigger>
            <TabsTrigger value="contact" className="flex-1 data-[state=active]:bg-[#8b6f47] data-[state=active]:text-[#f5ede3] text-[#5d4529]">
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('contactUs')}
            </TabsTrigger>
            <TabsTrigger value="incidents" className="flex-1 data-[state=active]:bg-[#8b6f47] data-[state=active]:text-[#f5ede3] text-[#5d4529]">
              <Wrench className="w-4 h-4 mr-2" />
              {t('incidentInfo')}
            </TabsTrigger>
          </TabsList>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className={`bg-white cursor-pointer transition-all hover:bg-[#faf6f1]`}
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-lg bg-[#8b6f47]/10 flex-shrink-0">
                          <faq.icon className="w-5 h-5 text-[#8b6f47]" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-[#8b6f47] text-xs">
                                {getCategoryLabel(faq.category)}
                              </Badge>
                              <h3 className="font-medium text-[#2d1810]">{faq.question}</h3>
                            </div>
                            {expandedFaq === index ? (
                              <ChevronUp className="w-5 h-5 text-[#6b5540] flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-[#6b5540] flex-shrink-0" />
                            )}
                          </div>
                          
                          <AnimatePresence>
                            {expandedFaq === index && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <p className="mt-4 text-sm text-[#5d4529] leading-relaxed">
                                  {faq.answer}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </TabsContent>

          {/* Contact Tab */}
          <TabsContent value="contact">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid md:grid-cols-2 gap-8"
            >
              {/* Contact Form */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#2d1810]">
                    <Mail className="w-5 h-5 text-[#8b6f47]" />
                    {language === 'ja' ? 'お問い合わせフォーム' : 'Contact Form'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmitContact} className="space-y-4">
                    <div>
                      <label className="text-sm text-[#5d4529] mb-1 block">
                        {language === 'ja' ? 'メールアドレス' : 'Email'}
                      </label>
                      <Input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="bg-[#f5ede3]"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#5d4529] mb-1 block">
                        {language === 'ja' ? '件名' : 'Subject'}
                      </label>
                      <Input
                        value={contactForm.subject}
                        onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                        className="bg-[#f5ede3]"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm text-[#5d4529] mb-1 block">
                        {language === 'ja' ? 'メッセージ' : 'Message'}
                      </label>
                      <Textarea
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="bg-[#f5ede3] min-h-32"
                        required
                      />
                    </div>
                    <Button type="submit" className="w-full bg-[#8b6f47] hover:bg-[#6b5540] text-[#f5ede3]">
                      {t('submit')}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Discord Support */}
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-[#2d1810]">
                    <svg className="w-5 h-5 text-[#5865F2]" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
                    </svg>
                    Discord
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-[#5d4529]">
                    {language === 'ja' 
                      ? 'Discordサーバーでは、他の学習者やサポートチームとリアルタイムでコミュニケーションができます。'
                      : 'On our Discord server, you can communicate in real-time with other learners and our support team.'}
                  </p>
                  <Button className="w-full bg-[#5865F2] hover:bg-[#4752C4] text-white">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {language === 'ja' ? 'Discordサーバーに参加' : 'Join Discord Server'}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-4"
            >
              {incidents.length > 0 ? (
                incidents.map((incident, index) => (
                  <Card key={index} className="bg-white">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${
                          incident.status === 'resolved' 
                            ? 'bg-emerald-500/20' 
                            : incident.status === 'investigating'
                            ? 'bg-amber-500/20'
                            : 'bg-rose-500/20'
                        }`}>
                          <AlertTriangle className={`w-5 h-5 ${
                            incident.status === 'resolved'
                              ? 'text-emerald-600'
                              : incident.status === 'investigating'
                              ? 'text-amber-600'
                              : 'text-rose-600'
                          }`} />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={
                              incident.status === 'resolved'
                                ? 'bg-emerald-500/20 text-emerald-600'
                                : incident.status === 'investigating'
                                ? 'bg-amber-500/20 text-amber-600'
                                : 'bg-rose-500/20 text-rose-600'
                            }>
                              {incident.status === 'resolved' 
                                ? (language === 'ja' ? '解決済み' : 'Resolved')
                                : incident.status === 'investigating'
                                ? (language === 'ja' ? '調査中' : 'Investigating')
                                : (language === 'ja' ? '発生中' : 'Ongoing')}
                            </Badge>
                            <span className="text-xs text-[#6b5540]">{incident.date}</span>
                          </div>
                          <h3 className="font-medium mb-1 text-[#2d1810]">{incident.title}</h3>
                          <p className="text-sm text-[#5d4529]">{incident.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <div className="text-center py-16">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <Wrench className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-[#2d1810]">
                    {language === 'ja' ? 'すべて正常に稼働中' : 'All Systems Operational'}
                  </h3>
                  <p className="text-[#6b5540]">
                    {language === 'ja' ? '現在、障害は発生していません' : 'No incidents reported'}
                  </p>
                </div>
              )}
            </motion.div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}